import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { jsPDF } from 'jspdf';
import { useToast } from '@/hooks/use-toast';
import { Helmet } from 'react-helmet';
import { trackVerifiedPurchase } from '@/lib/purchaseTracking';

const SuccessPage = () => {
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [accessCode, setAccessCode] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [skCopied, setSkCopied] = useState(false);
  const [purchaseVerified, setPurchaseVerified] = useState(false);
  const product = searchParams.get('product') || 'Service';
  const duration = searchParams.get('duration') || '24 Hours';
  const price = searchParams.get('price') || '£0.00';
  const source = searchParams.get('source');
  const sessionId = searchParams.get('session_id');
  const paypalToken = searchParams.get('token');
  const returnedAccessCode = searchParams.get('code') || '';
  const dmbClaimToken = source === 'dmb' && typeof window !== 'undefined'
    ? new URLSearchParams(window.location.hash.replace(/^#/, '')).get('claim') || ''
    : '';

  useEffect(() => {
    // Stripe flow: always verify the provider session with the backend, even
    // when the return URL already contains an access code. The code alone is
    // never treated as proof of payment for analytics.
    if (sessionId) {
      setLoading(true);
      const poll = async (attempts = 0) => {
        try {
          const res = await fetch(`https://api.phantompathvpn.com/api/checkout/verify?session_id=${sessionId}`);
          const data = await res.json();
          if (data.valid && data.accessCode) {
            setAccessCode(data.accessCode);
            if (data.secretKey) setSecretKey(data.secretKey);
            setPurchaseVerified(true);
            setLoading(false);
          } else if (data.pending && attempts < 15) {
            setTimeout(() => poll(attempts + 1), 3000);
          } else {
            if (!returnedAccessCode) setAccessCode('Processing... Please refresh in a moment.');
            setLoading(false);
          }
        } catch (err) {
          if (attempts < 5) {
            setTimeout(() => poll(attempts + 1), 3000);
          } else {
            if (!returnedAccessCode) setAccessCode('Error retrieving code. Please contact support.');
            setLoading(false);
          }
        }
      };
      poll();
    }

    // PayPal flow: fetch code using PayPal token (order ID)
    if (paypalToken && !sessionId) {
      setLoading(true);
      const poll = async (attempts = 0) => {
        try {
          const res = await fetch(`https://api.phantompathvpn.com/api/checkout/verify?session_id=${paypalToken}`);
          const data = await res.json();
          if (data.valid && data.accessCode) {
            setAccessCode(data.accessCode);
            if (data.secretKey) setSecretKey(data.secretKey);
            setPurchaseVerified(true);
            setLoading(false);
          } else if (attempts < 15) {
            setTimeout(() => poll(attempts + 1), 3000);
          } else {
            if (!returnedAccessCode) setAccessCode('Processing... Please refresh in a moment.');
            setLoading(false);
          }
        } catch (err) {
          if (attempts < 5) {
            setTimeout(() => poll(attempts + 1), 3000);
          } else {
            if (!returnedAccessCode) setAccessCode('Error retrieving code. Please contact support.');
            setLoading(false);
          }
        }
      };
      poll();
    }

    // Carrier-billing flow: the backend starts the payment with a random,
    // HttpOnly merchant reference. After the verified DMB webhook creates the
    // order, that exact reference can mint a short-lived opaque claim.
    if (source === 'dmb' && !sessionId && !paypalToken) {
      setLoading(true);
      const fetchCode = async (attempts = 0) => {
        try {
          let claimToken = dmbClaimToken;
          if (!claimToken && !returnedAccessCode) {
            const claimRes = await fetch('https://api.phantompathvpn.com/api/checkout/dmb-claim-session', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: '{}',
            });
            const claimData = await claimRes.json();
            if (claimData.pending && attempts < 20) {
              setTimeout(() => fetchCode(attempts + 1), 1500);
              return;
            }
            if (!claimData.ready || !claimData.claimToken) {
              throw new Error('Carrier-billing purchase is not ready');
            }
            claimToken = claimData.claimToken;
          }

          const res = claimToken
            ? await fetch('https://api.phantompathvpn.com/api/checkout/dmb-redeem', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ claimToken }),
              })
            : returnedAccessCode
              ? await fetch(`https://api.phantompathvpn.com/api/checkout/dmb-lookup?access_code=${encodeURIComponent(returnedAccessCode)}`)
              : null;
          if (!res) throw new Error('Missing carrier-billing purchase claim');
          const data = await res.json();
          const returnedCodeMatches = !returnedAccessCode || data.accessCode === returnedAccessCode;
          if (data.found && data.accessCode && returnedCodeMatches) {
            setAccessCode(data.accessCode);
            if (data.secretKey) setSecretKey(data.secretKey);
            setPurchaseVerified(true);
            if (claimToken) {
              window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
            }
          } else if (!returnedAccessCode) {
            setAccessCode('Processing... Please wait a moment and refresh.');
          }
        } catch (err) {
          if (!returnedAccessCode) setAccessCode('Error retrieving code. Please contact support.');
        }
        setLoading(false);
      };
      fetchCode();
    }

  }, []);

  useEffect(() => {
    if (!purchaseVerified) return;
    trackVerifiedPurchase({
      product,
      duration,
      price,
      verificationReference: sessionId || paypalToken || accessCode || source,
    });
  }, [purchaseVerified, product, duration, price, sessionId, paypalToken, accessCode, source]);

  const copyCode = () => {
    navigator.clipboard.writeText(accessCode);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2000);
    toast({ title: 'Copied', description: 'Access code copied to clipboard.' });
  };

  const copyKey = () => {
    navigator.clipboard.writeText(secretKey);
    setSkCopied(true);
    setTimeout(() => setSkCopied(false), 2000);
    toast({ title: 'Copied', description: 'Secret key copied to clipboard.' });
  };

  const handleDownloadPDF = () => {
    try {
      const doc = new jsPDF();
      let yPos = 20;
      const leftMargin = 20;
      const contentWidth = 170;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(0, 0, 0);
      doc.text('PHANTOMPATH – ACCESS SUMMARY', leftMargin, yPos);
      yPos += 10;

      doc.setFont('courier', 'bold');
      doc.setFontSize(12);
      doc.text('STATUS: CONNECTION ESTABLISHED', leftMargin, yPos);
      yPos += 15;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      const introText = 'Purchase confirmed. Your path is open. Your PhantomPath access is ready.';
      doc.text(doc.splitTextToSize(introText, contentWidth), leftMargin, yPos);
      yPos += 15;

      doc.setFont('helvetica', 'bold');
      doc.text('Protocols:', leftMargin, yPos);
      yPos += 8;

      doc.setFont('helvetica', 'normal');
      ['One Path, One Device', 'Time-limited access', 'No retained VPN activity logs'].forEach(protocol => {
        doc.text('  - ' + protocol, leftMargin + 5, yPos);
        yPos += 7;
      });
      yPos += 5;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.text('YOUR SESSION DETAILS', leftMargin, yPos);
      yPos += 10;

      doc.setFontSize(11);
      doc.text('Component', leftMargin, yPos);
      doc.text('Status', leftMargin + 80, yPos);
      yPos += 3;
      doc.setLineWidth(0.5);
      doc.line(leftMargin, yPos, leftMargin + contentWidth, yPos);
      yPos += 7;

      doc.setFont('helvetica', 'normal');
      const rows = [
        { col1: 'Product', col2: product },
        { col1: 'Duration', col2: duration },
        { col1: 'Price', col2: price },
        { col1: 'Access Code', col2: accessCode },
      ];
      if (secretKey) {
        rows.push({ col1: 'Secret Key', col2: secretKey });
      }
      rows.forEach(row => {
        doc.text(row.col1, leftMargin, yPos);
        doc.text(row.col2, leftMargin + 80, yPos);
        yPos += 8;
      });
      yPos += 10;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('LEGAL NOTICE', leftMargin, yPos);
      yPos += 8;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      const legalText = 'Service supplied following verified payment. Cancellation and refund rights depend on applicable law and service status. This confirmation does not remove statutory rights. IMPORTANT: PhantomPath numbers DO NOT support emergency calling (999, 112, or 911). You must maintain an alternative means of contacting emergency services.';
      const splitLegalText = doc.splitTextToSize(legalText, contentWidth);
      doc.text(splitLegalText, leftMargin, yPos);
      yPos += (splitLegalText.length * 5) + 10;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('COMPLIANCE / ADR', leftMargin, yPos);
      yPos += 8;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      const adrText = 'If a resolution is not reached within 6 weeks, we will provide a written Statement of Right to ADR to allow independent escalation to CISAS.';
      const splitAdrText = doc.splitTextToSize(adrText, contentWidth);
      doc.text(splitAdrText, leftMargin, yPos);
      yPos += (splitAdrText.length * 5) + 15;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('DURABLE RECORD', leftMargin, yPos);
      yPos += 8;

      doc.setFont('helvetica', 'italic');
      doc.setFontSize(10);
      doc.text('This is your durable record of service. Please download and retain it for your records.', leftMargin, yPos);

      doc.save('phantompath-contract-summary.pdf');

      toast({ title: "Briefing Downloaded", description: "Your secure contract summary has been saved to your device." });
    } catch {
      toast({ variant: "destructive", title: "Download Failed", description: "There was an error generating your secure document." });
    }
  };

  return (
    <div className="min-h-screen bg-[#050b14] pt-28 pb-20 px-4 flex flex-col items-center relative overflow-hidden">
      <Helmet><title>Success | PhantomPath Portal</title><meta name="robots" content="noindex, nofollow" /></Helmet>
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#00ffc8]/5 rounded-full blur-[120px] pointer-events-none" />

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full max-w-3xl relative z-10 flex flex-col items-center">
        <div className="w-full text-center mb-12">
          <h1 className="font-bold text-[clamp(1rem,4vw,1.5rem)] text-[#00ffc8] m-0 leading-relaxed uppercase break-words"
            style={{ fontFamily: "'Courier New', Courier, monospace", letterSpacing: '0.15em', textShadow: '0 0 10px rgba(0, 255, 200, 0.7)' }}>
            {purchaseVerified ? 'STATUS: CONNECTION ESTABLISHED' : 'PAYMENT VERIFICATION REQUIRED'}
          </h1>
          <div className="w-full h-px bg-[#00ffc8]/30 mt-6 shadow-[0_0_8px_rgba(0,255,200,0.5)]" />
        </div>

        <div className="w-full text-center py-6 sm:py-12 mb-8">
          <p className="text-white text-lg sm:text-xl md:text-2xl leading-relaxed max-w-2xl mx-auto"
            style={{ fontFamily: "'Courier New', Courier, monospace", letterSpacing: '0.05em' }}>
            {purchaseVerified
              ? 'Purchase confirmed. Your path is open. Save your access code and secret key below.'
              : 'Your service details will appear here after PhantomPath verifies payment and completes fulfilment.'}
          </p>
        </div>

        {loading ? (
          <div className="w-full bg-[#0a1120]/80 border border-[#00ffc8]/20 rounded-xl p-8 mb-8 text-center">
            <div className="inline-block w-8 h-8 border-2 border-[#00ffc8]/20 border-t-[#00ffc8] rounded-full animate-spin mb-4" />
            <p className="text-gray-400 text-sm font-mono">Retrieving your access credentials...</p>
          </div>
        ) : purchaseVerified ? (
          <>
            {/* Access Code */}
            <div className="w-full bg-[#0a1120]/80 border border-[#00ffc8]/20 rounded-xl p-6 mb-4 text-center">
              <p className="text-gray-500 text-[10px] font-mono uppercase tracking-widest mb-3">Your Access Code</p>
              <p className="text-[#00ffc8] text-lg sm:text-xl font-bold font-mono tracking-wider break-all whitespace-nowrap overflow-x-auto">{accessCode}</p>
              <button onClick={copyCode}
                className="mt-3 h-9 px-5 bg-[#00ffc8]/10 border border-[#00ffc8]/30 text-[#00ffc8] text-xs font-bold font-mono rounded-lg hover:bg-[#00ffc8]/20 active:scale-95 transition-all">
                {codeCopied ? '✓ COPIED' : '📋 COPY CODE'}
              </button>
            </div>

            {/* Secret Key */}
            {secretKey && (
              <div className="w-full bg-[#0a1120]/80 border border-[#f59e0b]/20 rounded-xl p-6 mb-8 text-center">
                <p className="text-gray-500 text-[10px] font-mono uppercase tracking-widest mb-3">Your Secret Recovery Key</p>
                <p className="text-[#f59e0b] text-lg sm:text-xl font-bold font-mono tracking-wider">{secretKey}</p>
                <button onClick={copyKey}
                  className="mt-3 h-9 px-5 bg-[#f59e0b]/10 border border-[#f59e0b]/30 text-[#f59e0b] text-xs font-bold font-mono rounded-lg hover:bg-[#f59e0b]/20 active:scale-95 transition-all">
                  {skCopied ? '✓ COPIED' : '🔑 COPY KEY'}
                </button>
                <p className="text-gray-600 text-[10px] font-mono mt-2">Save this key. It is used to recover your access code if lost.</p>
              </div>
            )}
          </>
        ) : (
          <div className="w-full bg-[#0a1120]/80 border border-[#68b8ff]/25 rounded-xl p-8 mb-8 text-center">
            <p className="text-gray-400 text-sm leading-relaxed">No verified payment was found for this page.</p>
          </div>
        )}

        {purchaseVerified && <div className="w-full border-2 border-[#00ffc8] rounded-lg p-6 bg-black/40 shadow-[0_0_15px_rgba(0,255,200,0.1)] backdrop-blur-sm mb-12">
          <p className="text-white text-sm sm:text-base leading-relaxed font-medium mb-4 text-center">
            LEGAL NOTICE: Service supplied following verified payment. Cancellation and refund rights depend on applicable law and service status. This confirmation does not remove statutory rights. IMPORTANT: PhantomPath numbers DO NOT support emergency calling (999, 112, or 911). You must maintain an alternative means of contacting emergency services.
          </p>
          <div className="h-px w-full bg-[#00ffc8]/30 my-4" />
          <p className="text-[#00ffc8]/80 text-xs sm:text-sm leading-relaxed text-center font-mono">
            ADR COMPLIANCE: If a resolution is not reached within 6 weeks, we will provide a written 'Statement of Right to ADR' to allow escalation to CISAS.
          </p>
        </div>}

        {purchaseVerified && <div className="w-full flex flex-col items-center gap-4 mb-8">
          <Button onClick={handleDownloadPDF}
            className="relative bg-[#00ffc8]/10 border border-[#00ffc8] text-[#00ffc8] hover:bg-[#00ffc8] hover:text-[#050b14] font-bold text-sm sm:text-base h-16 px-6 sm:px-10 rounded-xl transition-all shadow-[0_0_20px_rgba(0,255,200,0.4)] flex items-center justify-center gap-3 w-full sm:w-auto"
            style={{ animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}>
            <span className="text-xl">📥</span>
            <span className="tracking-wider text-center whitespace-normal sm:whitespace-nowrap leading-tight">
              SAVE CONTRACT SUMMARY & ACCESS RECEIPT
            </span>
          </Button>

          <Button onClick={() => window.location.href = 'https://portal.phantompathvpn.com'}
            className="bg-white/5 border border-white/10 text-white hover:bg-white/10 font-medium text-sm h-12 px-8 rounded-xl transition-all w-full sm:w-auto">
            Open Portal →
          </Button>

          <p className="text-gray-400 mt-2 text-sm font-medium text-center">
            This is your durable record of service. Please download and retain it.
          </p>
        </div>}
      </motion.div>
    </div>
  );
};

export default SuccessPage;
